import { Component, computed, inject, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'

import { AutoCompleteModule, AutoCompleteSelectEvent } from 'primeng/autocomplete'
import { InputTextModule } from 'primeng/inputtext'
import { Subject, debounceTime, switchMap, finalize, of } from 'rxjs'

import { Product } from 'app/_shared/interfaces/Product'
import { UsdPipe } from 'app/_shared/pipes/currency.pipe'
import { PricePipe } from 'app/_shared/pipes/price.pipe'
import { ProductService } from 'app/_shared/services/product.service'
import { AppConfigService } from 'app/app-config.service'
import { CartStore } from 'app/store/cart.store'

@Component({
  selector: 'app-search',
  imports: [FormsModule, InputTextModule, AutoCompleteModule, PricePipe, UsdPipe],
  templateUrl: './search.component.html',
  styleUrl: './search.component.scss'
})
export class SearchComponent implements OnInit {
  private productService = inject(ProductService)
  private appConfigService: AppConfigService = inject(AppConfigService)
  private router = inject(Router)
  private cartStore = inject(CartStore)

  isLoading = false
  search = ''
  private storefrontId: number
  products: Product[] = []
  disablePrices = false
  hidePrices = computed(() => this.cartStore.hideProductPrices())

  private readonly terms = new Subject<string>()

  ngOnInit() {
    this.storefrontId = this.appConfigService.getAppConfig?.id
    this.disablePrices = !!this.appConfigService.getAppConfig.disabled_prices

    this.terms
      .pipe(
        debounceTime(500),
        switchMap(q => {
          if (q.trim().length === 0) {
            this.isLoading = false
            return of({ data: { data: [] } })
          }

          return this.queryBackend(q).pipe(
            finalize(() => (this.isLoading = false)) // hide spinner on success, error or cancel
          )
        }),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: res => (this.products = res.data.data),
        error: () => (this.products = [])
      })
  }

  handleSearch(): void {
    this.isLoading = true // show spinner
    this.terms.next(this.search.trim())
  }

  private queryBackend(q: string) {
    const filters = { global_filter: q, sort_field: 'name', sort_order: 'asc' }
    return this.productService.getProducts(this.storefrontId, { ...filters, rows: 10, page: 1 })
  }

  goToProduct(evt: AutoCompleteSelectEvent): void {
    const product = evt.value as Product
    this.router.navigate(['products', product.slug])
    this.search = ''
    this.products = []
  }
}
